import { ARTICOLI, RACCOLTE, SLUG } from 'utils/endpoint'
import { ARTICOLO, LEZIONE, MEMBER, api, getImage } from 'utils'
import React, { useEffect, useRef, useState } from 'react'
import ArticoloHead from 'components/ArticoloHead'
import ArticoloSchemaOrg from 'components/richsnippet/ArticoloSchemaOrg'
import Breadcrumbs from 'components/atomic/Breadcrumbs'
import Error from 'next/error'
import FindItSide from 'components/FindItSide'
import MetaSeo from 'components/atomic/MetaSeo'
import Pills from 'components/atomic/Pills'
import PlaceholderReact from 'components/atomic/PlaceholderReact'
import RegisterBox from 'components/RegisterBox'
import { Bar, Correlati, StickyActions, StickySteps } from 'react-components/organisms'
import { api as pagebuilderApi } from 'react-pagebuilder/services/api'
import Credits from 'components/atomic/Credits'
import { useTrans } from 'hooks'
import Head from 'next/head'
import { useDispatch, useSelector } from 'react-redux'
import { setItem } from 'containers/toolbar'

const ArticoloDetail = ({
  item,
  pagina,
  newContent,
  newIntro,
  statusCode,
  lezioni,
  articoli,
  strumenti,
  findItems,
}) => {
  if (!item) return <Error statusCode={statusCode} />

  const t = useTrans()

  const dispatch = useDispatch()

  const { edit } = useSelector((state) => state.toolbar)

  const stickyStepsRef = useRef(null)
  const [stickyStepsVisible, setStickyStepsVisible] = useState(false)
  const [steps, setSteps] = useState([])

  useEffect(() => {
    if (articolo.tipologia === LEZIONE && !steps.length && !edit) initSteps()
  })

  const [articolo, setArticolo] = useState(item)

  const refresh = async () => {
    const { data } = await api.get(`${ARTICOLI}${articolo.id}/`)
    setArticolo(data)
    dispatch(setItem(data))
  }

  useEffect(() => {
    refresh()
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleStickyRef)
    window.addEventListener('scroll', handleSteps)

    return () => {
      window.removeEventListener('scroll', handleStickyRef)
      window.removeEventListener('scroll', handleSteps)
    }
  })

  function handleStickyRef() {
    setStickyStepsVisible(window.scrollY > stickyStepsRef.current.offsetTop - 50)
  }

  function initSteps() {
    const rawItems = Array.from(document.getElementsByClassName('block-menuitem'))

    if (!rawItems.length) return

    const items = rawItems.map((step) => ({
      label: step.attributes?.name?.value,
      id: step.id,
      active: false,
      done: false,
    }))
    setSteps(items)
  }

  function handleSteps() {
    const numSteps = steps.length

    if (!numSteps) return

    const items = steps.map((step, index) => {
      const nextIndex = index + 1
      const done = numSteps > nextIndex && getTop(steps[nextIndex].id) < 0
      return {
        ...step,
        active: !done && (numSteps === 1 || getTop(step.id) < 0),
        done,
      }
    })

    setSteps(items)
  }

  function getTop(id) {
    return document.getElementById(id)?.getBoundingClientRect()?.top
  }

  function getClass(baseClass) {
    return `${baseClass} ${baseClass}--${articolo.tipologia}`
  }

  function getLabelGTM() {
    return articolo.tipologia === LEZIONE ? 'lezione' : 'articolo'
  }

  const anteprima = !articolo.extra.has_perm

  const contentClass = `articolo-page__content ${anteprima ? 'anteprima' : ''}`

  const mainImage = getImage(articolo.main_image)

  return articolo ? (
    <>
      <MetaSeo
        seo={pagina.seo}
        additionalMetaTags={[
          {
            name: 'robots',
            content: 'noarchive',
          },
        ]}
        article
      />

      <ArticoloSchemaOrg articolo={articolo} />

      {mainImage && (
        <Head>
          <link rel="preload" href={mainImage} as="image" />
        </Head>
      )}

      <main className={getClass('articolo-page')}>
        <div className="articolo-page__breadcrumbs-container">
          <div className="w-container">
            <Breadcrumbs breadcrumbs={pagina.breadcrumbs} />
          </div>
        </div>
        <div className="articolo-page__sticky-container">
          <StickyActions item={articolo} labelGTM={getLabelGTM()} save share print />
        </div>
        <div className="articolo-page__wrapper">
          <div className={getClass('articolo-page__head-container')}>
            <ArticoloHead articolo={articolo} />
            {articolo.tipologia === LEZIONE ? (
              <Credits
                label={articolo.titolo_intro || t('Obiettivi, attività, materiali')}
                classes="articolo-page__summary"
                variant="white"
              >
                <PlaceholderReact
                  contentTypeId={articolo.model}
                  objectId={articolo.id}
                  pkey="intro"
                  serverSideContent={newIntro}
                />
              </Credits>
            ) : null}
          </div>
        </div>

        <div className={getClass('articolo-page__content-container')} ref={stickyStepsRef}>
          {articolo.tipologia === LEZIONE && (
            <StickySteps items={steps} visible={stickyStepsVisible} />
          )}

          <div className={contentClass}>
            {articolo.tipologia === ARTICOLO && mainImage && (
              <div className="articolo-page__content__imageContainer">
                <img
                  className="articolo-page__content__imageContainer__main-image"
                  src={mainImage}
                  alt={articolo.titolo?.toAltTitle()}
                  title={articolo.titolo?.toAltTitle()}
                />
                {articolo.copyright ? <span>{articolo.copyright}</span> : null}
              </div>
            )}
            {articolo.testo ? (
              <div
                className="custom-content"
                dangerouslySetInnerHTML={{ __html: articolo.testo }}
              />
            ) : (
              <PlaceholderReact
                contentTypeId={articolo.model}
                objectId={articolo.id}
                pkey="content"
                serverSideContent={newContent}
              />
            )}
            {!articolo.extra.has_perm && articolo.membership === MEMBER && (
              <>
                <RegisterBox variant="articolo" />
                <hr />
              </>
            )}
            <Pills tassonomia={articolo.tassonomia} />
            <FindItSide items={findItems} />
          </div>
        </div>
      </main>

      <Correlati articoli={articoli} strumenti={strumenti} lezioni={lezioni} />

      <Bar item={articolo} save share print labelGTM={getLabelGTM()} />
    </>
  ) : null
}

ArticoloDetail.getInitialProps = async ({ query: { slug }, res, req }) => {
  const headers = req?.headers?.cookie
    ? { cookie: req.headers.cookie, anteprima: req.headers.anteprima || false }
    : undefined

  try {
    const response = await api.get(`${ARTICOLI}${SLUG}${slug}/`, {
      headers,
      params: { server: true },
    })
    const item = response.data

    const pagina = item.pagina

    const newContent = await pagebuilderApi.getPagebuilder({
      contentTypeId: item.model,
      objectId: item.id,
      key: 'content',
      edit_mode: false,
      headers,
    })

    let newIntro = null
    if (item.tipologia === LEZIONE) {
      newIntro = await pagebuilderApi.getPagebuilder({
        contentTypeId: item.model,
        objectId: item.id,
        key: 'intro',
        edit_mode: false,
        headers,
      })
    }

    const toolbarProps = { toolbarItem: item, toolbarPage: pagina }

    const articoloResponse = await api.get(`${ARTICOLI}${item.id}/`)
    const articolo = articoloResponse.data

    const urlCorrelati = `${ARTICOLI}${articolo.id}/risorse_correlate/`
    const lezioniResponse = await api.get(urlCorrelati, {
      params: { limit: 3, excluded: 'raccolta/strumento/articolo' },
    })
    const lezioni = lezioniResponse.data.results

    const articoliResponse = await api.get(urlCorrelati, {
      params: { limit: 3, excluded: 'raccolta/strumento/lezione' },
    })
    const articoli = articoliResponse.data.results

    const strumentiResponse = await api.get(urlCorrelati, {
      params: { limit: 3, excluded: 'raccolta/lezione/articolo' },
    })
    const strumenti = strumentiResponse.data.results

    const params = { elementi__content_type_id: articolo.model, elementi__object_id: articolo.id }
    const raccolteItemsResponse = await api.get(RACCOLTE, { params })
    const findItems = {
      rivista: articolo.numero_rivista,
      raccolte: raccolteItemsResponse?.data?.results
        ? raccolteItemsResponse.data.results.slice(0, articolo.numero_rivista ? 2 : 3)
        : [],
    }

    return {
      item: articolo,
      pagina,
      newContent,
      newIntro,
      key: item.id,
      ...toolbarProps,
      lezioni,
      articoli,
      strumenti,
      findItems,
    }
  } catch (error) {
    if (res) {
      res.statusCode = 404
    }
    return { statusCode: 404 }
  }
}

export default ArticoloDetail
